import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  MagnifyingGlassIcon, 
  MapPinIcon,
  CheckIcon 
} from '@heroicons/react/24/outline';
import * as FlagIcons from 'country-flag-icons/react/3x2';

// --- Вспомогательные компоненты (оставляем без изменений) ---
const FlagComponent = ({ iso, className }) => {
  const Flag = FlagIcons[iso];
  if (!Flag) return <div className={`${className} bg-slate-200 rounded-sm`} />;
  return <Flag className={className} />;
};

const CustomSelect = ({ label, options, value, onChange, placeholder, showFlag = false, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const filtered = options?.filter(opt => 
    opt.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 100) || [];

  const selectedOption = options?.find(opt => 
    (opt.isoCode || opt.name) === (value?.isoCode || value?.name)
  );

  useEffect(() => {
    const handleClickOutside = (e) => { 
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false); 
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block text-[10px] font-black uppercase tracking-[2px] text-slate-400 mb-2 ml-2">{label}</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-4 bg-slate-50 border border-slate-200 rounded-[20px] transition-all duration-300 outline-none
          ${isOpen ? 'bg-white border-[#F65814] ring-4 ring-[#F65814]/5' : 'hover:border-slate-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-center gap-3 overflow-hidden">
          {showFlag && selectedOption && <FlagComponent iso={selectedOption.isoCode} className="w-5 h-3.5 object-cover rounded-sm shrink-0" />}
          {!showFlag && <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" />}
          <span className={`text-sm font-bold truncate ${selectedOption ? 'text-slate-900' : 'text-slate-300'}`}>
            {selectedOption ? selectedOption.name : placeholder}
          </span>
        </div>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#F65814]' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: 10 }} 
            className="absolute z-[100] mt-2 w-full bg-white border border-slate-100 rounded-[24px] shadow-[0_25px_60px_rgba(0,0,0,0.15)] overflow-hidden"
          >
            <div className="p-3 border-b border-slate-50 flex items-center gap-2">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />
              <input 
                autoFocus 
                className="w-full text-xs font-bold outline-none bg-transparent" 
                placeholder="Type to search..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
            <div className="max-h-[220px] overflow-y-auto p-1 custom-scrollbar">
              {filtered.length > 0 ? filtered.map((opt, idx) => (
                <button 
                  key={`${opt.name}-${idx}`} 
                  type="button" 
                  onClick={() => { onChange(opt); setIsOpen(false); setSearch(''); }} 
                  className="w-full flex items-center justify-between px-4 py-3 rounded-[16px] hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    {showFlag && <FlagComponent iso={opt.isoCode} className="w-5 h-3.5 object-cover rounded-sm" />}
                    <span className="text-xs font-bold text-slate-700 group-hover:text-[#F65814] transition-colors">{opt.name}</span>
                  </div>
                  {(value?.name === opt.name) && <CheckIcon className="w-4 h-4 text-[#F65814]" />}
                </button>
              )) : (
                <div className="p-4 text-center text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                  {search ? "No results found" : "Start typing..."}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- ОСНОВНОЙ КОМПОНЕНТ С ДОБАВЛЕНИЕМ ШТАТА ---
const LocationSelector = ({ selectedCountry, selectedState, selectedCity, onLocationChange }) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [lib, setLib] = useState(null);

  useEffect(() => {
    import("country-state-city").then(csc => {
      setLib(csc);
      setCountries(csc.Country.getAllCountries());
    });
  }, []);

  // Загрузка штатов при выборе страны
  useEffect(() => {
    if (selectedCountry && lib) {
      const allStates = lib.State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(allStates);
      // Если в стране нет штатов (редкий случай), можно сразу грузить города, 
      // но для консистентности бэкенда лучше оставить пустым или сбросить.
    } else {
      setStates([]);
    }
  }, [selectedCountry, lib]);

  // Загрузка городов при выборе штата
  useEffect(() => {
    if (selectedCountry && selectedState && lib) {
      const allCities = lib.City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
      setCities(allCities);
    } else {
      setCities([]);
    }
  }, [selectedState, selectedCountry, lib]);

  if (!lib) return <div className="h-14 w-full bg-slate-50 animate-pulse rounded-[20px]" />;

  return (
    <div className="space-y-4">
      {/* Страна */}
      <CustomSelect 
        label="Country" 
        options={countries} 
        value={selectedCountry} 
        showFlag={true} 
        placeholder="Select Country" 
        onChange={(val) => onLocationChange({ country: val, state: null, city: null })} 
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ШТАТ (НОВЫЙ ИНПУТ) */}
        <CustomSelect 
          label="State / Region" 
          options={states} 
          value={selectedState} 
          disabled={!selectedCountry || states.length === 0} 
          placeholder={!selectedCountry ? "Pick country first" : "Select State"} 
          onChange={(val) => onLocationChange({ country: selectedCountry, state: val, city: null })} 
        />

        {/* Город */}
        <CustomSelect 
          label="City" 
          options={cities} 
          value={selectedCity} 
          disabled={!selectedState} 
          placeholder={!selectedState ? "Pick state first" : "Select City"} 
          onChange={(val) => onLocationChange({ country: selectedCountry, state: selectedState, city: val })} 
        />
      </div>
    </div>
  );
};

export default LocationSelector;